// src/pages/admin/AdminCommunity.js
// Manage both Community feeds — Notices and Meet Locals — add, edit,
// approve/hide/delete, same pattern as AdminSkills.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllNotices, getAllMeetLocals, updateNotice, updateMeetLocal, deleteNotice, deleteMeetLocal, createNotice, createMeetLocal } from '../../lib/db';
import { useAuth } from '../../lib/AuthContext';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const NOTICE_CAT_LABELS = { general: 'General', lost_found: 'Lost & Found', roadworks: 'Roadworks', club_news: 'Club News', free_stuff: 'Free Stuff' };
const ACTIVITY_LABELS = { walk: 'A walk', coffee: 'Coffee or a chat', hobby: 'A hobby or activity', sport: 'Sport or fitness', other: 'Something else' };

const EMPTY_NOTICE = { category: 'general', title: '', description: '' };
const EMPTY_MEETLOCAL = { activityType: 'walk', title: '', description: '' };

const AdminCommunity = () => {
  const navigate = useNavigate();
  const { user, isTownAdmin, townAdminScope } = useAuth();
  const [tab, setTab] = useState('notices');
  const [notices, setNotices] = useState([]);
  const [meetLocals, setMeetLocals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_NOTICE);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [n, m] = await Promise.all([getAllNotices(), getAllMeetLocals()]);
    setNotices(isTownAdmin ? n.filter(x => x.townId === townAdminScope) : n);
    setMeetLocals(isTownAdmin ? m.filter(x => x.townId === townAdminScope) : m);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const approve = async (id, isNotice) => {
    if (isNotice) await updateNotice(id, { status: 'live' });
    else await updateMeetLocal(id, { status: 'live' });
    toast.success('Approved — now live');
    load();
  };

  const hide = async (id, isNotice) => {
    if (isNotice) await updateNotice(id, { status: 'hidden' });
    else await updateMeetLocal(id, { status: 'hidden' });
    toast.success('Hidden from public view');
    load();
  };

  const handleDelete = async (id, title, isNotice) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    if (isNotice) await deleteNotice(id);
    else await deleteMeetLocal(id);
    toast.success('Deleted');
    load();
  };

  const startEdit = (item, isNotice) => {
    setForm(isNotice
      ? { category: item.category || 'general', title: item.title || '', description: item.description || '' }
      : { activityType: item.activityType || 'walk', title: item.title || '', description: item.description || '' }
    );
    setEditingId(item.id);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => { setForm(tab === 'notices' ? EMPTY_NOTICE : EMPTY_MEETLOCAL); setAdding(false); setEditingId(null); };

  const switchTab = (t) => { setTab(t); cancelForm(); setForm(t === 'notices' ? EMPTY_NOTICE : EMPTY_MEETLOCAL); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const isNotice = tab === 'notices';
      if (editingId) {
        if (isNotice) await updateNotice(editingId, form);
        else await updateMeetLocal(editingId, form);
        toast.success('Updated!');
      } else {
        const townId = isTownAdmin ? townAdminScope : (localStorage.getItem('gather-town-id') || 'loughrea');
        if (isNotice) await createNotice({ ...form, townId, postedBy: user.uid });
        else await createMeetLocal({ ...form, townId, postedBy: user.uid });
        toast.success('Added!');
      }
      cancelForm();
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const list = (tab === 'notices' ? notices : meetLocals)
    .filter(l => statusFilter === 'all' || l.status === statusFilter);
  const labels = tab === 'notices' ? NOTICE_CAT_LABELS : ACTIVITY_LABELS;

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>← Admin</button>
      <div style={s.h1}>Community</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => switchTab('notices')} style={{ ...s.tabBtn, ...(tab === 'notices' ? s.tabActive : {}) }}>
          Noticeboard ({notices.filter(n => n.status === 'pending_review').length} pending)
        </button>
        <button onClick={() => switchTab('meetlocals')} style={{ ...s.tabBtn, ...(tab === 'meetlocals' ? s.tabActive : {}) }}>
          Local Meet-ups ({meetLocals.filter(m => m.status === 'pending_review').length} pending)
        </button>
      </div>

      <button
        onClick={() => (adding ? cancelForm() : (setForm(tab === 'notices' ? EMPTY_NOTICE : EMPTY_MEETLOCAL), setAdding(true)))}
        style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 14 }}
      >
        {adding ? '✕ Cancel' : tab === 'notices' ? '+ Add to Noticeboard' : '+ Add Local Meet-up'}
      </button>

      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>{editingId ? '✏️ Edit' : '+ New'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tab === 'notices' ? (
              <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
                {Object.entries(NOTICE_CAT_LABELS).map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
              </select>
            ) : (
              <select className="form-input" value={form.activityType} onChange={e => set('activityType', e.target.value)}>
                {Object.entries(ACTIVITY_LABELS).map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
              </select>
            )}
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Title" />
            <textarea className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description" rows={3} style={{ resize: 'vertical' }} />
            <button onClick={handleSave} disabled={saving} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
        <option value="all">All statuses</option>
        <option value="live">Live</option>
        <option value="pending_review">Pending review</option>
        <option value="hidden">Hidden</option>
        <option value="expired">Expired</option>
      </select>

      {list.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Nothing here.</div>
      ) : list.map(item => (
        <div key={item.id} style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ ...s.badge, background: 'var(--sage-pale)', color: 'var(--sage-dark)', marginBottom: 6 }}>
                {labels[item.category || item.activityType] || 'Other'}
              </div>
              <div style={s.title}>{item.title}</div>
            </div>
            <span style={{
              ...s.badge,
              background: item.status === 'live' ? 'var(--sage-pale)' : item.status === 'pending_review' ? '#FFF3CD' : '#F0F0EE',
              color: item.status === 'live' ? 'var(--sage-deep)' : item.status === 'pending_review' ? '#8A6D1D' : 'var(--muted)',
            }}>
              {item.status === 'pending_review' ? 'Pending review' : item.status === 'hidden' ? 'Hidden' : item.status === 'expired' ? 'Expired' : 'Live'}
            </span>
          </div>
          {item.description && <div style={s.desc}>{item.description}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {item.status !== 'live' && item.status !== 'expired' && (
              <button style={s.approveBtn} onClick={() => approve(item.id, tab === 'notices')}>Approve</button>
            )}
            {item.status === 'live' && (
              <button style={s.editBtn} onClick={() => hide(item.id, tab === 'notices')}>Hide</button>
            )}
            <button style={s.editBtn} onClick={() => startEdit(item, tab === 'notices')}>Edit</button>
            <button style={s.deleteBtn} onClick={() => handleDelete(item.id, item.title, tab === 'notices')}>Delete</button>
          </div>
        </div>
      ))}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  page: { padding: '52px 20px 40px', background: 'var(--cream)', minHeight: '100vh' },
  back: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 12 },
  h1: { fontSize: 22, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  tabBtn: { flex: 1, padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--white)', color: 'var(--ink)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  tabActive: { background: 'var(--sage-dark)', color: 'white', border: 'none' },
  filterSelect: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)', marginBottom: 16, boxSizing: 'border-box' },
  card: { background: 'var(--white)', borderRadius: 14, padding: 14, marginBottom: 10, border: '1.5px solid var(--border)' },
  badge: { display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 100, padding: '4px 10px' },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--ink)' },
  desc: { fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 },
  approveBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-deep)', color: 'white', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  editBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  deleteBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: '#FBEAE7', color: '#C0392B', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
};

export default AdminCommunity;
